/* eslint-disable @typescript-eslint/dot-notation */
import { Injectable } from '@angular/core';
import { DataModel, SurroundingIntervalsModel } from '@shared';
import { WorkspaceDatabaseProvider } from '../workspace-database.provider';
import { WorkspaceDatabaseKeys } from '../schemas';
import { combineLatest, Observable, of } from 'rxjs';
import { RxCollection, RxDocument } from 'rxdb';
import { map, switchMap } from 'rxjs/operators';
import { maxBy, minBy } from 'lodash';
import { addRxPlugin } from 'rxdb';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { QueryOperatorsEnum } from '../models';
addRxPlugin(RxDBQueryBuilderPlugin);

@Injectable()
export class FlatTableDataRepository {
	public constructor(private dbProvider: WorkspaceDatabaseProvider) {}

	public getOverlappingData$(
		tableName: string,
		projectNumber: string,
		excludedId: number,
		depthFrom: number,
		depthTo: number
	): Observable<DataModel> {
		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.findOne({
				selector: {
					$and: [
						{
							[WorkspaceDatabaseKeys.internalColumns.rxdbid]: {
								[QueryOperatorsEnum.notEqual]: excludedId
							},
							[WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber },
							[WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: false }
						},
						{
							$or: [
								{
									// overlap before
									$and: [
										{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $gt: depthFrom } },
										{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthFrom } },
										{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthTo } },
										{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gte: depthTo } }
									]
								},
								{
									// overlap after
									$and: [
										{
											$or: [
												{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthFrom } },
												{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $in: [depthFrom] } }
											]
										},
										{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthFrom } },
										{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthTo } },
										{ [WorkspaceDatabaseKeys.columns.depthTo]: { $lt: depthTo } }
									]
								},
								{
									// overlap wrap
									$and: [
										{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $gt: depthFrom } },
										{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthFrom } },
										{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthTo } },
										{ [WorkspaceDatabaseKeys.columns.depthTo]: { $lt: depthTo } }
									]
								},
								{
									// overlap inner before depthTo
									$and: [
										{
											$or: [
												{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthFrom } },
												{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $in: [depthFrom] } }
											]
										},
										{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthFrom } },
										{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthTo } },
										{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthTo } }
									]
								},
								{
									// overlap inner after depthFrom
									$and: [
										{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthFrom } },
										{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: depthFrom } },
										{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthTo } },
										{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gte: depthTo } }
									]
								}
							]
						}
					]
				}
			})
			.$.pipe(map((doc: RxDocument) => doc?.toJSON()));
	}

	public getDuplicateDataByDepths$(
		tableName: string,
		projectNumber: string,
		excludedIds: number[],
		depthFrom: number,
		depthTo: number
	): Observable<boolean> {
		const conditions = excludedIds.map((id) => ({
			[WorkspaceDatabaseKeys.internalColumns.rxdbid]: { [QueryOperatorsEnum.notEqual]: id }
		}));
		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find({
				selector: {
					$and: [
						...conditions,
						{ [WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber } },
						{ [WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: false } },
						{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $in: [depthFrom] } },
						{ [WorkspaceDatabaseKeys.columns.depthTo]: { $in: [depthTo] } }
					]
				}
			})
			.limit(1)
			.$.pipe(map((items: RxDocument<DataModel>[]) => items?.length !== 0));
	}

	public getOutsideIntervalData$(
		tableName: string,
		projectNumber: string,
		excludedId: number,
		depthFrom: number,
		depthTo: number
	): Observable<DataModel[]> {
		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find({
				selector: {
					$and: [
						{
							[WorkspaceDatabaseKeys.internalColumns.rxdbid]: {
								[QueryOperatorsEnum.notEqual]: excludedId
							}
						},
						{ [WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber } },
						{ [WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: false } }
					]
				}
			})
			.$.pipe(
				map((items: RxDocument<DataModel>[]) => items.map((item) => item.toJSON())),
				// todo: this is a fix for RxDB $eq for numbers is not working on Android
				// $lte and $gte not working properly on Android
				// using multiple $in condition did not work
				map((items) => items.filter((x) => depthFrom >= Number(x.depth_from) && depthTo <= Number(x.depth_to)))
			);
	}

	public getSurroundingIntervals$(
		tableName: string,
		projectNumber: string,
		excludedId: number,
		depthFrom: number,
		depthTo: number
	): Observable<SurroundingIntervalsModel> {
		return combineLatest([
			this.dbProvider
				.getDatabaseCollection(tableName.toLowerCase())
				.find({
					selector: {
						$and: [
							{
								[WorkspaceDatabaseKeys.internalColumns.rxdbid]: {
									[QueryOperatorsEnum.notEqual]: excludedId
								}
							},
							{ [WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber } },
							{ [WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: false } },
							{
								$or: [
									{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $lt: depthFrom } },
									{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $in: [depthFrom] } }
								]
							}
						]
					}
				})
				.$.pipe(
					map((items: RxDocument<DataModel>[]) => {
						const closestPrev = maxBy(items, 'depth_to');
						return closestPrev?.toJSON();
					})
				),
			this.dbProvider
				.getDatabaseCollection(tableName.toLowerCase())
				.find({
					selector: {
						$and: [
							{
								[WorkspaceDatabaseKeys.internalColumns.rxdbid]: {
									[QueryOperatorsEnum.notEqual]: excludedId
								}
							},
							{ [WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber } },
							{ [WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: false } },
							{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gte: depthTo } }
						]
					}
				})
				.$.pipe(
					map((items: RxDocument<DataModel>[]) => {
						const closestAfter = minBy(items, 'depth_from');
						return closestAfter?.toJSON();
					})
				)
		]).pipe(
			map((surroundingIntervals: [DataModel, DataModel]) => {
				const surrounding: SurroundingIntervalsModel = {
					previousInterval: surroundingIntervals[0],
					nextInterval: surroundingIntervals[1]
				};

				return surrounding;
			})
		);
	}

	public hasAnyChildTableData$(tableName: string, projectNumber: string, excludedId: number): Observable<boolean> {
		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find({
				selector: {
					$and: [
						{
							[WorkspaceDatabaseKeys.internalColumns.rxdbid]: {
								[QueryOperatorsEnum.notEqual]: excludedId
							}
						},
						{ [WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber } },
						{ [WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: false } }
					]
				}
			})
			.limit(1)
			.$.pipe(map((anyData: DataModel[]) => (anyData.length > 0 ? true : false)));
	}

	public hasAnyStartsAtZeroData$(tableName: string, projectNumber: string, excludedId: number): Observable<boolean> {
		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find({
				selector: {
					$and: [
						{
							[WorkspaceDatabaseKeys.internalColumns.rxdbid]: {
								[QueryOperatorsEnum.notEqual]: excludedId
							}
						},
						{ [WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber } },
						{ [WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: false } },
						{ [WorkspaceDatabaseKeys.columns.depthFrom]: { $eq: 0 } },
						{ [WorkspaceDatabaseKeys.columns.depthTo]: { $gt: 0 } }
					]
				}
			})
			.limit(1)
			.$.pipe(map((items: RxCollection<DataModel>[]) => items?.length !== 0));
	}

	public isTablePropertyValueUnique$(
		tableName: string,
		projectNumber: string,
		property: string,
		propertyValue: string,
		excludedId: number,
		includeSoftDeleted: boolean
	): Observable<boolean> {
		if (!tableName) {
			return of(true);
		}
		const regexp = RegExp('^' + propertyValue.trim() + '$', 'i');

		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find({
				selector: {
					$and: [
						{
							[WorkspaceDatabaseKeys.internalColumns.rxdbid]: {
								[QueryOperatorsEnum.notEqual]: excludedId
							}
						},
						{ [WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber } },
						{ [WorkspaceDatabaseKeys.internalColumns.isDeleted]: { $eq: includeSoftDeleted } },
						// The classic {$eq: keyValue} is case sensitive, so performing uniqueness checks on a field will
						// result in undesired functionality (eg. DDH !== ddh). Using RegExp with case insensitive will solve the issue.
						{ [property]: { $regex: regexp } }
					]
				}
			})
			.limit(1)
			.$.pipe(map((item: RxDocument<DataModel>[]) => item.length === 0));
	}

	public getLastPropertyValue$(
		tableName: string | null,
		property: string,
		projectNumber: string
	): Observable<string | number | null> {
		if (!tableName) {
			return of(null);
		}
		return this.dbProvider
			.getDatabaseCollection(tableName.toLowerCase())
			.find({
				selector: {
					$and: [
						{ [WorkspaceDatabaseKeys.internalColumns.rxdbid]: { [QueryOperatorsEnum.notEqual]: 0 } },
						{ [WorkspaceDatabaseKeys.columns.projectNumber]: { $eq: projectNumber } }
					]
				}
			})
			.sort({
				[WorkspaceDatabaseKeys.internalColumns.rxdbid]: 'desc'
			})
			.limit(1)
			.$.pipe(
				switchMap((items: RxDocument<DataModel>[]) =>
					items.length !== 0
						? items.map((tableData: RxDocument<DataModel>) =>
								tableData && tableData[property] ? tableData[property] : null
						  )
						: of(null)
				)
			);
	}
}
